import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Instagram, Globe, Briefcase, Users, Linkedin, Phone, Code, Building2, Trophy, GraduationCap, TrendingUp, ArrowUpRight, Youtube } from 'lucide-react';
import CodeversityLogo from './CodeversityLogo';
import { useSiteContact } from '../hooks/useSiteContact';

const WhatsAppIcon = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Footer = () => {
  const contact = useSiteContact();
  return (
    <footer style={{ background: '#0A0A0A', fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Main Footer */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-16 lg:py-20">
        {/* Top: Brand + CTA */}
        <div className="flex flex-col lg:flex-row items-start justify-between gap-8 mb-16 pb-16" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <CodeversityLogo size="md" showIcon={true} />
            <p className="text-sm mt-3 max-w-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Evaluate. Excel. Transform.
            </p>
            <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
              India's verified developer platform for hiring, learning & growth.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/codearena" className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold tracking-wide text-white transition-all"
              style={{ background: '#002FA7', borderRadius: '2px' }}
              data-testid="footer-arena-cta">
              <Code size={14} /> EvalentX Arena <ArrowUpRight size={12} />
            </Link>
            <Link to="/company" className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-all"
              style={{ border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '2px' }}
              data-testid="footer-company-cta">
              <Building2 size={14} /> For Companies
            </Link>
            <Link to="/colleges" className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-all"
              style={{ border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '2px' }}
              data-testid="footer-colleges-cta">
              <GraduationCap size={14} /> For Colleges
            </Link>
          </div>
        </div>

        {/* Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Quick Links */}
          <div>
            <h3 className="text-[10px] font-bold tracking-[0.2em] uppercase mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Navigation
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'Home', to: '/' },
                { label: 'Company Page', to: '/company' },
                { label: 'EvalentX Arena', to: '/codearena' },
                { label: 'Colleges Page', to: '/colleges' },
                { label: 'Register', to: '/register' },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h3 className="text-[10px] font-bold tracking-[0.2em] uppercase mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Programs
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'Job Board', to: '/codearena/jobs' },
                { label: 'Hiring Challenges', to: '/codearena/hiring-challenges' },
                { label: 'Contests', to: '/codearena/contests' },
                { label: 'Events', to: '/codearena/events' },
                { label: 'Mock Interview AI', to: '/codearena/mock-interview' },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            {/* Referral */}
            <div className="mt-6 p-3" style={{ border: '1px solid rgba(16,185,129,0.2)', background: 'rgba(16,185,129,0.05)', borderRadius: '2px' }}>
              <Link to="/referral" className="flex items-center gap-2 text-sm font-medium" style={{ color: '#10B981' }}>
                <Users size={14} /> Join Referral Program
              </Link>
              <p className="text-[10px] mt-1.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                Refer friends & earn rewards
              </p>
            </div>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-[10px] font-bold tracking-[0.2em] uppercase mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Resources
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'College Rankings', to: '/codearena/college-rankings', icon: Trophy },
                { label: 'National Analytics', to: '/codearena/analytics', icon: TrendingUp },
                { label: 'College Portal', to: '/college/auth', icon: GraduationCap },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="flex items-center gap-2 text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    <link.icon size={12} style={{ color: 'rgba(255,255,255,0.3)' }} />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-[10px] font-bold tracking-[0.2em] uppercase mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Legal & Support
            </h3>
            <ul className="space-y-3">
              {[
                { label: 'Terms & Conditions', to: '/terms' },
                { label: 'Refund Policy', to: '/terms#refund' },
                { label: 'Contact Us', to: '/contact' },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/contact?tab=investor" className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-white" style={{ color: '#10B981' }}>
                  <Briefcase size={12} /> Investor Corner
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-[10px] font-bold tracking-[0.2em] uppercase mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Get in Touch
            </h3>
            <ul className="space-y-3 mb-6">
              <li>
                <a href="mailto:support@evalentx.com" className="flex items-center gap-2 text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  <Mail size={14} style={{ color: 'rgba(255,255,255,0.3)' }} /> support@evalentx.com
                </a>
              </li>
              <li>
                <a href={contact.phone_href} className="flex items-center gap-2 text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.55)' }} data-testid="footer-phone-link">
                  <Phone size={14} style={{ color: 'rgba(255,255,255,0.3)' }} /> {contact.phone}
                </a>
              </li>
              {contact.whatsapp_enabled && (
                <li>
                  <a href={contact.whatsapp_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 transition-colors hover:bg-[#25D366]/20"
                    style={{ color: '#25D366', border: '1px solid rgba(37,211,102,0.35)', borderRadius: '2px' }} data-testid="footer-whatsapp-chat-link">
                    <WhatsAppIcon size={14} /> Chat on WhatsApp
                  </a>
                </li>
              )}
              <li>
                <a href="https://www.evalentx.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  <Globe size={14} style={{ color: 'rgba(255,255,255,0.3)' }} /> www.evalentx.com
                </a>
              </li>
            </ul>
            {/* Social */}
            <div className="flex gap-2">
              <a href="https://www.linkedin.com/company/evalentx/about/" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center transition-all hover:border-white/30 hover:text-white"
                style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', borderRadius: '2px' }}
                title="LinkedIn" data-testid="footer-linkedin-link">
                <Linkedin size={16} />
              </a>
              <a href="https://www.instagram.com/evalentx?utm_source=qr&igsh=OHZrejJ0a3dwOXlh" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center transition-all hover:border-white/30 hover:text-white"
                style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', borderRadius: '2px' }}
                title="Instagram" data-testid="footer-instagram-link">
                <Instagram size={16} />
              </a>
              {contact.whatsapp_channel_url && (
              <a href={contact.whatsapp_channel_url} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center transition-all hover:border-white/30 hover:text-white"
                style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', borderRadius: '2px' }}
                title="WhatsApp Channel" data-testid="footer-whatsapp-link">
                <WhatsAppIcon size={16} />
              </a>
              )}
              <a href="https://www.youtube.com/@EvalentX" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center transition-all hover:border-white/30 hover:text-white"
                style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', borderRadius: '2px' }}
                title="YouTube" data-testid="footer-youtube-link">
                <Youtube size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "'IBM Plex Mono', monospace" }}>
            &copy; 2026 EvalentX. All rights reserved.
          </p>

          {/* Startup India Certified Badge */}
          <a href="https://www.startupindia.gov.in" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-2 transition-all hover:opacity-80"
            style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px', background: 'rgba(255,255,255,0.03)' }}
            data-testid="footer-startup-india-badge"
            title="DPIIT Recognised Startup"
          >
            {/* Startup India Tricolor Step Mark */}
            <svg width="28" height="22" viewBox="0 0 28 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Startup India Logo">
              <rect x="0" y="14" width="8" height="8" rx="1" fill="#FF9933" />
              <rect x="10" y="7" width="8" height="15" rx="1" fill="#FFFFFF" />
              <rect x="20" y="0" width="8" height="22" rx="1" fill="#138808" />
            </svg>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold tracking-[0.12em] uppercase" style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.2' }}>
                Startup India
              </span>
              <span className="text-[9px] font-medium tracking-[0.08em] uppercase" style={{ color: 'rgba(255,255,255,0.35)', lineHeight: '1.3' }}>
                DPIIT Recognised
              </span>
            </div>
          </a>

          <p className="text-[10px] tracking-[0.15em] uppercase font-bold" style={{ color: 'rgba(255,255,255,0.15)' }}>
            Evaluate. Excel. Transform.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
